/**
 * File: plainBooks.ts
 * Path: packages/config/src/accounting/plainBooks.ts
 * Description: The books in everyday words, for someone who isn't an
 * accountant: plain names for the five account types, balances shown the
 * way round people expect, the four headline figures, and the everyday
 * entries ("I paid for something", "Money came in") that write the proper
 * double entry behind the scenes. Liam, 2026-10-08: easy for
 * non-accountants, but still full accounting underneath.
 */

import type {
  AccountType,
  Journal,
  JournalDraft,
  JournalLine,
  LedgerAccount,
  ProfileKind,
} from "./accountingTypes";
import { isDebitNatured, JOURNAL_LIMITS, type TrialBalance } from "./ledger";

/** What each account type is called on screen, with the proper term beside it. */
export const PLAIN_TYPE_LABELS: Record<ProfileKind, Record<AccountType, string>> = {
  company: {
    asset: "What the business owns",
    liability: "What the business owes",
    equity: "Owners' stake",
    income: "Money earned",
    expense: "Money spent",
  },
  personal: {
    asset: "What you own",
    liability: "What you owe",
    equity: "Your net worth",
    income: "Money earned",
    expense: "Money spent",
  },
};

/**
 * A balance the way round people read it: positive for money in the bank,
 * a debt owed, sales made or money spent. Negative only when an account
 * has gone the unusual way (an overdrawn bank account).
 */
export const plainCents = (account: Pick<LedgerAccount, "type">, balanceCents: number): number =>
  isDebitNatured(account.type) ? balanceCents : -balanceCents;

export interface BooksFigures {
  /** Money in bank and cash accounts (cards are in `oweCents`). */
  bankCents: number;
  /** Clients who owe the business, or (personal) what the company owes you. */
  owedToYouCents: number;
  /** Everything owed: cards, SARS, suppliers, loans. */
  oweCents: number;
  /** Of `oweCents`, what the company owes its director. */
  directorLoanCents: number;
  /** Profit (or, personal, surplus) for the year so far. */
  profitCents: number;
  /** Waiting in Suspense for someone to say what it was. */
  suspenseCents: number;
}

/** The four headline figures from a trial balance. */
export const booksFigures = (tb: TrialBalance): BooksFigures => {
  const sum = (keep: (a: LedgerAccount) => boolean) =>
    tb.rows.filter((r) => keep(r.account)).reduce((s, r) => s + plainCents(r.account, r.balanceCents), 0);
  return {
    bankCents: sum((a) => a.money && a.type === "asset"),
    owedToYouCents: sum((a) => a.role === "debtors" || a.role === "loanToCompany"),
    oweCents: sum((a) => a.type === "liability"),
    directorLoanCents: sum((a) => a.role === "directorLoan"),
    profitCents: tb.profitCents,
    suspenseCents: Math.abs(sum((a) => a.role === "suspense")),
  };
};

/* ---------------------------------------------------------------------------
 * Everyday entries
 * ------------------------------------------------------------------------ */

export type EverydayKind = "spent" | "received" | "ownIn" | "ownBack" | "transfer";

export interface EverydayEntry {
  kind: EverydayKind;
  label: string;
  hint: string;
  /** What the category picker is called; null when there isn't one. */
  categoryLabel: string | null;
  /** What the money account picker is called. */
  moneyLabel: string;
}

export const everydayEntries = (profile: ProfileKind): EverydayEntry[] =>
  profile === "company"
    ? [
        {
          kind: "spent",
          label: "I paid for something",
          hint: "A bill, a subscription, a tool or anything else the business paid for.",
          categoryLabel: "What it was for",
          moneyLabel: "Paid from",
        },
        {
          kind: "received",
          label: "Money came in",
          hint: "A client paid, or the bank paid interest.",
          categoryLabel: "What it was for",
          moneyLabel: "Paid into",
        },
        {
          kind: "ownIn",
          label: "I put my own money in",
          hint: "You lent the business money from your own account. It owes you this back.",
          categoryLabel: null,
          moneyLabel: "Paid into",
        },
        {
          kind: "ownBack",
          label: "The business paid me back",
          hint: "The business repaid some of what you lent it. This isn't income or a cost.",
          categoryLabel: null,
          moneyLabel: "Paid from",
        },
        {
          kind: "transfer",
          label: "I moved money between accounts",
          hint: "From one of the business's accounts to another, or paying off a card.",
          categoryLabel: null,
          moneyLabel: "Moved from",
        },
      ]
    : [
        {
          kind: "spent",
          label: "I spent money",
          hint: "Groceries, fuel, medical, a subscription: anything you paid for.",
          categoryLabel: "What it was for",
          moneyLabel: "Paid from",
        },
        {
          kind: "received",
          label: "Money came in",
          hint: "Pay from IDK Solutions, a salary, interest or anything else you received.",
          categoryLabel: "What it was for",
          moneyLabel: "Paid into",
        },
        {
          kind: "ownIn",
          label: "I lent money to IDK Solutions",
          hint: "Money you paid into the company's account. It owes you this back.",
          categoryLabel: null,
          moneyLabel: "Paid from",
        },
        {
          kind: "ownBack",
          label: "IDK Solutions paid me back",
          hint: "The company repaid some of what you lent it. This isn't income.",
          categoryLabel: null,
          moneyLabel: "Paid into",
        },
        {
          kind: "transfer",
          label: "I moved money between accounts",
          hint: "From one of your accounts to another, or paying off your card.",
          categoryLabel: null,
          moneyLabel: "Moved from",
        },
      ];

export interface EverydayForm {
  kind: EverydayKind;
  date: string;
  description: string;
  amountCents: number;
  /** The expense or income account (spent / received). */
  categoryId: string;
  /** Where the money left from or arrived in. */
  moneyId: string;
  /** Transfers only: where it went. */
  toMoneyId: string;
  /** Company "spent" only: you paid it yourself, so the business owes you. */
  paidPersonally: boolean;
}

const live = (accounts: readonly LedgerAccount[]) => accounts.filter((a) => !a.archived);

/** The accounts each picker offers. */
export const everydayChoices = (
  kind: EverydayKind,
  accounts: readonly LedgerAccount[],
): { categories: LedgerAccount[]; money: LedgerAccount[] } => {
  const all = live(accounts);
  const money = all.filter((a) => a.money);
  if (kind === "spent") {
    // Expenses, plus things bought to keep (equipment), never a role account.
    const categories = all.filter(
      (a) => a.type === "expense" || (a.type === "asset" && !a.money && a.role === null),
    );
    return { categories, money };
  }
  if (kind === "received") {
    // Income, plus a client settling an invoice already recorded.
    return { categories: all.filter((a) => a.type === "income" || a.role === "debtors"), money };
  }
  return { categories: [], money };
};

const debit = (accountId: string, cents: number, memo = ""): JournalLine => ({
  accountId,
  debitCents: cents,
  creditCents: 0,
  memo,
});
const credit = (accountId: string, cents: number, memo = ""): JournalLine => ({
  accountId,
  debitCents: 0,
  creditCents: cents,
  memo,
});

/**
 * The journal an everyday entry stands for, or why it can't be made yet.
 * The server still checks the result like any other journal.
 */
export const everydayJournal = (
  form: EverydayForm,
  profile: ProfileKind,
  accounts: readonly LedgerAccount[],
): { draft: JournalDraft | null; issues: string[] } => {
  const issues: string[] = [];
  const entry = everydayEntries(profile).find((e) => e.kind === form.kind);
  const all = live(accounts);
  const find = (id: string) => all.find((a) => a.id === id);
  const byRole = (role: LedgerAccount["role"]) => all.find((a) => a.role === role);
  const cents = form.amountCents;

  if (!Number.isInteger(cents) || cents <= 0) issues.push("Enter the amount.");
  else if (cents > JOURNAL_LIMITS.lineCents) issues.push("That amount is too large to be right.");

  const money = find(form.moneyId);
  const needsMoney = !(form.kind === "spent" && form.paidPersonally && profile === "company");
  if (needsMoney && !money?.money) issues.push(`Choose where it was ${entry?.moneyLabel.toLowerCase() ?? "paid"}.`);

  const category = find(form.categoryId);
  const { categories } = everydayChoices(form.kind, accounts);
  if ((form.kind === "spent" || form.kind === "received") && !categories.some((a) => a.id === category?.id)) {
    issues.push("Choose what it was for.");
  }

  const loanRole = profile === "company" ? "directorLoan" : "loanToCompany";
  const loan = byRole(loanRole);
  const usesLoan =
    form.kind === "ownIn" || form.kind === "ownBack" || (form.kind === "spent" && form.paidPersonally && profile === "company");
  if (usesLoan && !loan) {
    issues.push(
      profile === "company"
        ? "These books have no director's loan account. Add one under Accounts first."
        : "These books have no loan-to-company account. Add one under Accounts first.",
    );
  }

  const to = find(form.toMoneyId);
  if (form.kind === "transfer") {
    if (!to?.money) issues.push("Choose where it was moved to.");
    else if (to.id === money?.id) issues.push("Choose two different accounts.");
  }

  const description = form.description.trim();
  if (description.length > JOURNAL_LIMITS.narration) issues.push("Make the description shorter.");
  if (issues.length > 0) return { draft: null, issues };

  const lines: JournalLine[] = (() => {
    switch (form.kind) {
      case "spent":
        return [
          debit(category!.id, cents),
          form.paidPersonally && profile === "company"
            ? credit(loan!.id, cents, "Paid personally by the director")
            : credit(money!.id, cents),
        ];
      case "received":
        return [debit(money!.id, cents), credit(category!.id, cents)];
      case "ownIn":
        return profile === "company"
          ? [debit(money!.id, cents), credit(loan!.id, cents)]
          : [debit(loan!.id, cents), credit(money!.id, cents)];
      case "ownBack":
        return profile === "company"
          ? [debit(loan!.id, cents), credit(money!.id, cents)]
          : [debit(money!.id, cents), credit(loan!.id, cents)];
      case "transfer":
        return [debit(to!.id, cents), credit(money!.id, cents)];
    }
  })();

  return {
    draft: { date: form.date, narration: description || entry!.label, lines },
    issues: [],
  };
};

/** Did money arrive in, or leave, the bank and card accounts? */
export const moneyDirection = (
  journal: Pick<Journal, "lines">,
  accounts: readonly LedgerAccount[],
): "in" | "out" | "none" => {
  const money = new Set(accounts.filter((a) => a.money).map((a) => a.id));
  const net = journal.lines
    .filter((l) => money.has(l.accountId))
    .reduce((s, l) => s + l.debitCents - l.creditCents, 0);
  return net > 0 ? "in" : net < 0 ? "out" : "none";
};

/* ---------------------------------------------------------------------------
 * Statements
 * ------------------------------------------------------------------------ */

export interface StatementRow {
  account: LedgerAccount;
  /** Shown the way round people expect (see plainCents). */
  cents: number;
}

export interface ProfitAndLoss {
  from: string;
  to: string;
  income: StatementRow[];
  expenses: StatementRow[];
  incomeCents: number;
  expenseCents: number;
  profitCents: number;
}

const byCode = (a: StatementRow, b: StatementRow) =>
  a.account.code.localeCompare(b.account.code, "en", { numeric: true });

/** Income and expenses for any period, both dates included. */
export const profitAndLoss = (
  journals: readonly Journal[],
  accounts: readonly LedgerAccount[],
  from: string,
  to: string,
): ProfitAndLoss => {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const totals = new Map<string, number>();
  for (const journal of journals) {
    if (journal.date < from || journal.date > to) continue;
    for (const line of journal.lines) {
      const account = byId.get(line.accountId);
      if (!account || (account.type !== "income" && account.type !== "expense")) continue;
      totals.set(account.id, (totals.get(account.id) ?? 0) + line.debitCents - line.creditCents);
    }
  }
  const rows = (type: AccountType) =>
    [...totals.entries()]
      .map(([id, balance]) => ({ account: byId.get(id)!, cents: plainCents(byId.get(id)!, balance) }))
      .filter((r) => r.account.type === type && r.cents !== 0)
      .sort(byCode);
  const income = rows("income");
  const expenses = rows("expense");
  const incomeCents = income.reduce((s, r) => s + r.cents, 0);
  const expenseCents = expenses.reduce((s, r) => s + r.cents, 0);
  return { from, to, income, expenses, incomeCents, expenseCents, profitCents: incomeCents - expenseCents };
};

export interface BalanceSheet {
  asAt: string;
  assets: StatementRow[];
  liabilities: StatementRow[];
  equity: StatementRow[];
  assetsCents: number;
  liabilitiesCents: number;
  /** Equity accounts plus this year's profit so far. */
  equityCents: number;
  profitCents: number;
}

/** What is owned, owed and left over at the trial balance's date. */
export const balanceSheet = (tb: TrialBalance): BalanceSheet => {
  const rows = (type: AccountType) =>
    tb.rows
      .filter((r) => r.account.type === type)
      .map((r) => ({ account: r.account, cents: plainCents(r.account, r.balanceCents) }))
      .filter((r) => r.cents !== 0);
  const assets = rows("asset");
  const liabilities = rows("liability");
  const equity = rows("equity");
  const total = (list: StatementRow[]) => list.reduce((s, r) => s + r.cents, 0);
  return {
    asAt: tb.asAt,
    assets,
    liabilities,
    equity,
    assetsCents: total(assets),
    liabilitiesCents: total(liabilities),
    equityCents: total(equity) + tb.profitCents,
    profitCents: tb.profitCents,
  };
};
